import { AuthProvider, HttpError } from "react-admin";
import { API_GATEWAY_URL } from "./apiGateway";
import {
  clearAuthStorage,
  getStoredToken,
  getStoredUser,
  persistToken,
  persistUser,
} from "./authStorage";

type AuthRequestOptions = RequestInit & { skipAuth?: boolean };

const apiRequest = async (endpoint: string, options: AuthRequestOptions = {}) => {
  const { skipAuth, ...fetchOptions } = options;
  const token = getStoredToken();
  const headers: HeadersInit = {
    "Content-Type": "application/json",
    ...(fetchOptions.headers || {}),
  };

  if (!skipAuth && token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(`${API_GATEWAY_URL}${endpoint}`, {
    ...fetchOptions,
    headers,
  });

  if (!response.ok) {
    let errorPayload: unknown = null;
    try {
      errorPayload = await response.json();
    } catch (err) {
      errorPayload = await response.text();
    }

    throw new HttpError(
      (errorPayload as { error?: string })?.error || response.statusText,
      response.status,
      errorPayload,
    );
  }

  return response;
};

export const authProvider: AuthProvider = {
  login: async ({ username, password }) => {
    const normalizedLogin = String(username ?? "").trim();
    const response = await apiRequest("/auth/login", {
      method: "POST",
      body: JSON.stringify({ login: normalizedLogin, password }),
      skipAuth: true,
    });

    const data = await response.json();
    persistToken(data.token);
    persistUser(data.user);

    return Promise.resolve();
  },
  logout: async () => {
    const token = getStoredToken();

    if (token) {
      try {
        await apiRequest("/auth/logout", {
          method: "POST",
        });
      } catch (error) {
        // swallow logout errors
        console.error("Logout error:", error);
      }
    }

    clearAuthStorage();
    return Promise.resolve();
  },
  checkError: ({ status }) => {
    if (status === 401 || status === 403) {
      clearAuthStorage();
      return Promise.reject();
    }
    return Promise.resolve();
  },
  checkAuth: async () => {
    const token = getStoredToken();

    if (!token) {
      clearAuthStorage();
      return Promise.reject();
    }

    try {
      const response = await apiRequest("/auth/me", { method: "GET" });
      const payload = await response.json();
      if (payload?.user) {
        persistUser(payload.user);
      }
      return Promise.resolve();
    } catch (error) {
      clearAuthStorage();
      return Promise.reject();
    }
  },
  getPermissions: () => {
    const currentUser = getStoredUser() as { role?: string } | null;
    return Promise.resolve(currentUser?.role);
  },
  getIdentity: () => Promise.resolve(getStoredUser()),
};

export default authProvider;
